import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { fabric } from 'fabric';
import useEditorStore from '../store/useEditorStore';
import { getEmojiCategories, EmojiFile, EmojiCategory } from '../data/emojiData';

const EmojiPickerContainer = styled.div<{ $position: 'below' | 'above'; $left: number; $top: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  background: ${props => props.theme.panel || '#ffffff'};
  border: 1px solid ${props => props.theme.border || '#e0e0e0'};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  width: 300px;
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const EmojiGrid = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
`;

const CategorySection = styled.div`
  margin-bottom: 16px;
`;

const CategoryTitle = styled.h3`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.textSecondary || '#666666'};
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const EmojiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  margin-bottom: 8px;
  text-align:center;
`;

const EmojiItem = styled.button`
  width: 25px;
  height: 25px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  padding: 4px;

  &:hover {
    background: ${props => props.theme.buttonHover || '#e8e8e8'};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid ${props => props.theme.border || '#e0e0e0'};
  background: transparent;
  color: ${props => props.theme.text || '#000000'};
  font-size: 14px;
  outline: none;

  &::placeholder {
    color: ${props => props.theme.textSecondary || '#666666'};
  }
`;

const Header = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${props => props.theme.border || '#e0e0e0'};
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.text || '#000000'};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.theme.border || '#e0e0e0'};
  border-top: 2px solid ${props => props.theme.primary || '#4285f4'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.textSecondary || '#666666'};
  cursor: pointer;
  font-size: 16px;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${props => props.theme.text || '#000000'};
  }
`;

interface EmojiPickerProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement>;
  canvas?: fabric.Canvas | null;
  onEmojiAdded?: () => void;
}


const EmojiPicker: React.FC<EmojiPickerProps> = ({ isOpen, onClose, triggerRef, canvas: propCanvas, onEmojiAdded }) => {
  const { canvas: storeCanvas, addToHistory } = useEditorStore();
  const canvas = propCanvas || storeCanvas;
  const [emojis, setEmojis] = useState<EmojiFile[]>([]);
  const [categorizedEmojis, setCategorizedEmojis] = useState<EmojiCategory[]>([]);
  const [filteredEmojis, setFilteredEmojis] = useState<EmojiFile[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [position, setPosition] = useState<'below' | 'above'>('below');
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);

  // Load emoji files
  useEffect(() => {
    const loadEmojis = async () => {
      try {
        const IMAGE_SERVER_URL = import.meta.env.VITE_IMAGE_SERVER_URL || 'http://localhost:5002';
        
        // Get categorized emojis with face emojis first
        const emojiCategories = getEmojiCategories(IMAGE_SERVER_URL);
        
        // Flatten all emojis for search functionality
        const allEmojis = emojiCategories.flatMap(category => category.emojis);

        setCategorizedEmojis(emojiCategories);
        setEmojis(allEmojis);
        setFilteredEmojis(allEmojis);
      } catch (error) {
        console.error('Error loading emojis:', error);
      }
    };

    if (isOpen) {
      loadEmojis();
    }
  }, [isOpen]);

  // Filter emojis based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEmojis(emojis);
    } else {
      const filtered = emojis.filter(emoji =>
        emoji.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEmojis(filtered);
    }
  }, [searchTerm, emojis]);

  // Calculate picker position relative to the trigger element
  const calculatePickerPosition = () => {
    if (!triggerRef?.current) return;

    const triggerElement = triggerRef.current;
    const triggerRect = triggerElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const pickerHeight = 400; // max-height from CSS
    const pickerWidth = 300; // width from CSS

    // Calculate horizontal position (position to the right of the button)
    let left = triggerRect.right + 8; // 8px gap from the button

    // Ensure the picker doesn't go off the right edge
    if (left + pickerWidth > viewportWidth) {
      // If not enough space on right, position to the left of the button
      left = triggerRect.left - pickerWidth - 8;

      // If still not enough space, align to right edge
      if (left < 10) {
        left = viewportWidth - pickerWidth - 10; // 10px margin from edge
      }
    }

    // Ensure the picker doesn't go off the left edge
    if (left < 10) {
      left = 10; // 10px margin from edge
    }

    // Align vertically with the button (center align)
    let top: number;
    let newPosition: 'below' | 'above';

    // Center the picker vertically with the button
    const buttonCenterY = triggerRect.top + (triggerRect.height / 2);
    top = buttonCenterY - (pickerHeight / 2);

    // If picker goes above viewport, align to top
    if (top < 10) {
      top = 10;
      newPosition = 'below';
    }
    // If picker goes below viewport, align to bottom
    else if (top + pickerHeight > viewportHeight - 10) {
      top = viewportHeight - pickerHeight - 10;
      newPosition = 'above';
    } else {
      newPosition = 'below';
    }

    setPosition(newPosition);
    setPickerPosition({ top, left });
  };

  // Calculate position based on available space
  useEffect(() => {
    if (!isOpen || !triggerRef?.current) return;
    calculatePickerPosition();
  }, [isOpen, triggerRef]);

  // Handle window resize to recalculate position
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && triggerRef?.current) {
        calculatePickerPosition();
      }
    };

    if (isOpen) {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen, triggerRef]);

  // Handle click outside to close picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const tryFallbackMethod = (emojiPath: string) => {
    fabric.Image.fromURL(emojiPath, (img) => {
      if (!canvas) return;
      
      const emojiSize = 100;
      const scale = emojiSize / Math.max(img.width || 128, img.height || 128);
      
      const randomX = Math.random() * (canvas.width! - emojiSize);
      const randomY = Math.random() * (canvas.height! - emojiSize);
      
      img.set({
        left: Math.max(0, randomX),
        top: Math.max(0, randomY),
        scaleX: scale,
        scaleY: scale,
        selectable: true,
        evented: true,
        name: 'emoji',
        visible: true,
        opacity: 1,
        originX: 'left',
        originY: 'top',
        angle: 0,
        skewX: 0,
        skewY: 0
      });
      
      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      
      if (addToHistory) {
        addToHistory();
      }
      
      if (onEmojiAdded) {
        onEmojiAdded();
      }
      
      onClose();
    }, { crossOrigin: 'anonymous' });
  };

  const addEmojiToCanvas = async (emojiPath: string) => {
    if (!canvas) return;

    setIsLoading(true);
    try {
      // Fetch the emoji content and convert to base64
      const response = await fetch(emojiPath);
      if (!response.ok) {
        throw new Error(`Failed to fetch emoji: ${response.status} ${response.statusText}`);
      }
      
      let emojiContent = await response.text();

      // Ensure SVG has proper dimensions for Fabric.js
      if (emojiContent.includes('<svg')) {
        // Add width and height if not present
        if (!emojiContent.includes('width=')) {
          emojiContent = emojiContent.replace(
            /<svg([^>]*)>/,
            '<svg$1 width="128" height="128">'
          );
        }
        
        // Ensure viewBox is present for proper scaling
        if (!emojiContent.includes('viewBox=')) {
          emojiContent = emojiContent.replace(
            /<svg([^>]*)>/,
            '<svg$1 viewBox="0 0 128 128">'
          );
        }
      }
      
      // Convert SVG to base64 data URL
      const base64Content = btoa(emojiContent);
      const dataUrl = `data:image/svg+xml;base64,${base64Content}`;

      // Convert SVG to canvas first to handle complex gradients properly
      const tempCanvas = document.createElement('canvas');
      const ctx = tempCanvas.getContext('2d');
      tempCanvas.width = 128;
      tempCanvas.height = 128;
      
      const img = new Image();
      img.onload = () => {
        // Draw the SVG onto the canvas
        ctx?.drawImage(img, 0, 0, 128, 128);
        
        // Convert canvas to data URL
        const canvasDataUrl = tempCanvas.toDataURL('image/png');

        // Load the rasterized emoji as an image using the canvas data URL
        fabric.Image.fromURL(canvasDataUrl, (fabricImg) => {
        if (!canvas) return;

        // Set emoji properties
        const emojiSize = 100; // Target size for emoji
        
        // Try to get the original size first, then fallback to current size or default
        const originalSize = fabricImg.getOriginalSize();
        const actualWidth = originalSize.width || fabricImg.width || 128;
        const actualHeight = originalSize.height || fabricImg.height || 128;
        
        const scale = emojiSize / Math.max(actualWidth, actualHeight);
        
        // Position emoji in a random location within the canvas
        const randomX = Math.random() * (canvas.width! - emojiSize);
        const randomY = Math.random() * (canvas.height! - emojiSize);
        
        fabricImg.set({
          left: Math.max(0, randomX),
          top: Math.max(0, randomY),
          scaleX: scale,
          scaleY: scale,
          selectable: true,
          evented: true,
          name: 'emoji',
          visible: true,
          opacity: 1,
          originX: 'left',
          originY: 'top',
          angle: 0,
          skewX: 0,
          skewY: 0
        } as any);
        
        // Force the image to the target size if scaling didn't work properly
        if (Math.abs(fabricImg.getScaledWidth() - emojiSize) > 10) {
          const currentWidth = fabricImg.getScaledWidth();
          const currentHeight = fabricImg.getScaledHeight();
          const newScaleX = emojiSize / currentWidth;
          const newScaleY = emojiSize / currentHeight;
          fabricImg.set({
            scaleX: newScaleX,
            scaleY: newScaleY
          });
        }
        
        // Store the emoji as base64 data (like uploaded images)
        (fabricImg as any).dataUrl = canvasDataUrl;
        (fabricImg as any).src = canvasDataUrl;

        // Add to canvas
        canvas.add(fabricImg);
        canvas.setActiveObject(fabricImg);

        // Force multiple renders to ensure visibility
        canvas.renderAll();
        setTimeout(() => {
          canvas.renderAll();
        }, 100);
        setTimeout(() => {
          canvas.renderAll();
        }, 500);

        // Add to history (only if available)
        if (addToHistory) {
          addToHistory();
        }

        // Call the onEmojiAdded callback if provided
        if (onEmojiAdded) {
          onEmojiAdded();
        }

        // Close the picker
        onClose();
        }, { crossOrigin: 'anonymous' });
      };
      
      img.onerror = (error) => {
        console.error('SVG image failed to load:', error);
        // Try fallback method immediately
        tryFallbackMethod(emojiPath);
      };
      
      img.src = dataUrl;
    } catch (error) {
      console.error('Error adding emoji to canvas:', error);
      tryFallbackMethod(emojiPath);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <EmojiPickerContainer 
      id="emoji-picker-container"
      ref={pickerRef} 
      $position={position}
      $left={pickerPosition.left}
      $top={pickerPosition.top}
    >
      <Header>
        Choose an Emoji
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>
      <SearchInput
        type="text"
        placeholder="Search emojis..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <EmojiGrid>
        {isLoading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <LoadingSpinner />
          </div>
        )}
        {!isLoading && searchTerm.trim() === '' && categorizedEmojis.map((category, categoryIndex) => (
          <CategorySection key={categoryIndex}>
            <CategoryTitle>{category.name}</CategoryTitle>
            <EmojiRow>
              {category.emojis.map((emoji, emojiIndex) => (
                <EmojiItem
                  key={emojiIndex}
                  onClick={() => addEmojiToCanvas(emoji.path)}
                  title={emoji.displayName}
                >
                  <img
                    src={emoji.path}
                    alt={emoji.displayName}
                    onError={(e) => {
                      // Hide broken images
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </EmojiItem>
              ))}
            </EmojiRow>
          </CategorySection>
        ))}
        {!isLoading && searchTerm.trim() !== '' && (
          <EmojiRow>
            {filteredEmojis.map((emoji, index) => (
              <EmojiItem
                key={index}
                onClick={() => addEmojiToCanvas(emoji.path)}
                title={emoji.displayName}
              >
                <img
                  src={emoji.path}
                  alt={emoji.displayName}
                  onError={(e) => {
                    // Hide broken images
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </EmojiItem>
            ))}
          </EmojiRow>
        )}
        {!isLoading && filteredEmojis.length === 0 && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
            No emojis found
          </div>
        )}
      </EmojiGrid>
    </EmojiPickerContainer>
  );
};

export default EmojiPicker;